import { useMutation, useQuery } from "@tanstack/react-query"
import { supabase } from "@nasti/common/supabase"
import { queryClient } from "@nasti/common/utils"
import type {
  ActiveSubBatch,
  Container,
  StorageLocation,
} from "@nasti/common/types"
import { useMemo } from "react"

import { TESTING_TEST_HISTORY_KEY } from "@/hooks/useTestingTestHistory"

type SubBatchContainer = Pick<Container, "id" | "name" | "purpose" | "active">

type ActiveSubBatchWithContainer = ActiveSubBatch & {
  container: SubBatchContainer | null
}

export type SubBatchWithStorage = ActiveSubBatchWithContainer & {
  current_storage?: {
    id: string
    location_id: string
    stored_at: string | null
    notes: string | null
    location: StorageLocation
  } | null
}

// Everything that shows where a batch's bags are stored. The bag list reads
// current storage under its own key, keyed by bag ids rather than the batch,
// so refetching the bag list alone keeps showing the old location.
export const invalidateSubBatchStorage = (batchId: string) => {
  for (const queryKey of [
    ["subBatches", batchId],
    ["subBatches", "storage"],
    ["subBatches", "currentStorage", batchId],
    ["subBatches", "storageHistory", batchId],
    ["batches", "detail", batchId],
    ["batches", "byFilter"],
    ["storageLocations"],
  ]) {
    queryClient.invalidateQueries({ queryKey })
  }
}

// Query: Get sub-batches for a batch
export const useSubBatches = (batchId: string) => {
  const { data, ...rest } = useQuery({
    queryKey: ["subBatches", batchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("active_sub_batches")
        .select(
          "*, container:containers!sub_batches_container_id_fkey(id, name, purpose, active)",
        )
        .eq("batch_id", batchId)
        .order("created_at", { ascending: true })
        .overrideTypes<ActiveSubBatchWithContainer[], { merge: false }>()

      if (error) throw new Error(error.message)
      return data
    },
    enabled: Boolean(batchId),
  })

  const ids = data?.map((sb) => sb.id)
  const storageQuery = useQuery({
    enabled: Boolean(ids && ids?.length && ids.length > 0),
    queryKey: ["subBatches", "storage", ids],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("batch_storage")
        .select(
          "id, location_id, stored_at, notes, sub_batch_id, location:storage_locations(*)",
        )
        .in("sub_batch_id", ids ?? [])
        .is("moved_out_at", null)
        .order("stored_at", { ascending: false })

      if (error) throw new Error(error.message)
      return data
    },
  })

  const result = useMemo(() => {
    const storageData = new Map(
      storageQuery.data?.map((st) => [st.sub_batch_id, st]),
    )
    return data?.map((sb) => ({
      ...sb,
      current_storage: storageData.get(sb.id),
    }))
  }, [storageQuery.data, data])

  return { ...rest, ...storageQuery, data: result }
}

// Mutation: Split a sub-batch into one or more new sub-batches
type SplitSubBatchOutput = {
  weight_grams: number
  notes?: string
  container_id?: string
  location_id?: string
}

type SplitSubBatchParams = {
  subBatchId: string
  outputs: SplitSubBatchOutput[]
}

export const useSplitSubBatch = () => {
  return useMutation<string[], Error, SplitSubBatchParams>({
    mutationFn: async ({ subBatchId, outputs }) => {
      const { data, error } = await supabase.rpc("fn_split_sub_batch", {
        p_sub_batch_id: subBatchId,
        p_outputs: outputs,
      })

      if (error) throw new Error(error.message)
      return data as string[]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subBatches"] })
      queryClient.invalidateQueries({ queryKey: ["batches"] })
      queryClient.invalidateQueries({ queryKey: ["containers"] })
      // A Testing organisation's inventory lists the bags it holds
      queryClient.invalidateQueries({ queryKey: ["assignments"] })
      // New bags carry the tests of the bags they came from
      queryClient.invalidateQueries({ queryKey: TESTING_TEST_HISTORY_KEY })
    },
  })
}

// Mutation: Merge sub-batches within a batch
type MergeSubBatchesParams = {
  subBatchIds: string[]
  containerId?: string
  locationId?: string
  notes?: string
}

export const useMergeSubBatches = () => {
  return useMutation<string, Error, MergeSubBatchesParams>({
    mutationFn: async ({ subBatchIds, containerId, locationId, notes }) => {
      const { data, error } = await supabase.rpc("fn_merge_sub_batches", {
        p_sub_batch_ids: subBatchIds,
        ...(containerId && { p_container_id: containerId }),
        ...(locationId && { p_location_id: locationId }),
        p_notes: notes,
      })

      if (error) throw new Error(error.message)
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subBatches"] })
      queryClient.invalidateQueries({ queryKey: ["batches"] })
      queryClient.invalidateQueries({ queryKey: ["storageLocations"] })
      queryClient.invalidateQueries({ queryKey: ["containers"] })
      queryClient.invalidateQueries({ queryKey: ["assignments"] })
      // New bags carry the tests of the bags they came from
      queryClient.invalidateQueries({ queryKey: TESTING_TEST_HISTORY_KEY })
    },
  })
}
